import React from 'react';
import {
  Box, IconButton, Skeleton, Table, TableBody, TableCell, TableHead, TableRow,
  TableSortLabel, Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StatusChip from '../../../components/StatusChip';
import { formatDate } from '../../../utils';
import { IPD_COLORS } from '../../../theme';

/** Same formula as PoList — equal-width badges so PARTIALLY RETURNED and DRAFT align. */
const STATUS_CHIP_MIN_WIDTH = Math.ceil('PARTIALLY RETURNED'.length * 8 + 24);

const COLUMNS = [
  { key: 'returnId', label: 'Return ID', sortable: true },
  { key: 'ward', label: 'Ward', sortable: true },
  { key: 'patient_id', label: 'Patient', sortable: true },
  { key: 'admission_id', label: 'Admission', sortable: true },
  { key: 'date', label: 'Date', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'actions', label: 'Actions', sortable: false },
];

const headCell = {
  color: IPD_COLORS.textSecondary,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

export default function WardReturnsTable({
  rows = [],
  sort,
  onSortChange,
  onView,
  onAcknowledge,
  onReject,
  acknowledgeDisabled = false,
  rejectDisabled = false,
  loading = false,
  skeletonRows = 8,
}) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table
        size="small"
        aria-label="Ward return requests"
        sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}
      >
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => {
              const active = Boolean(col.sortable && sort?.key === col.key);
              return (
                <TableCell
                  key={col.key}
                  sx={headCell}
                  sortDirection={active ? sort.dir : false}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={active}
                      direction={active ? sort.dir : 'asc'}
                      onClick={() => onSortChange?.(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? Array.from({ length: skeletonRows }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                {COLUMNS.map((col) => (
                  <TableCell key={col.key}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))
            : rows.map((row) => {
              const hasWard = row.ward && row.ward !== '—';
              return (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => onView?.(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ fontWeight: 600, color: IPD_COLORS.primary }}>
                    {row.returnId}
                  </TableCell>
                  <TableCell sx={hasWard
                    ? { fontFamily: 'monospace', fontSize: 12 }
                    : { color: IPD_COLORS.textMuted }}>
                    {hasWard ? row.ward : 'Not Assigned'}
                  </TableCell>
                  <TableCell>{row.patient_id}</TableCell>
                  <TableCell>{row.admission_id}</TableCell>
                  <TableCell>{formatDate(row.date)}</TableCell>
                  <TableCell>
                    <StatusChip status={row.status} minWidth={STATUS_CHIP_MIN_WIDTH} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View details">
                        <IconButton
                          size="small"
                          sx={{ color: IPD_COLORS.primary }}
                          aria-label={`View details for ${row.returnId}`}
                          onClick={() => onView?.(row)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {row.status === 'SUBMITTED' && (onAcknowledge || onReject) && (
                        <>
                          {onAcknowledge && (
                            <Tooltip title="Acknowledge">
                              <span>
                                <IconButton
                                  size="small"
                                  sx={{ color: IPD_COLORS.success.text }}
                                  aria-label={`Acknowledge ${row.returnId}`}
                                  onClick={() => onAcknowledge(row)}
                                  disabled={acknowledgeDisabled}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {onReject && (
                            <Tooltip title="Reject">
                              <span>
                                <IconButton
                                  size="small"
                                  sx={{ color: IPD_COLORS.error.text }}
                                  aria-label={`Reject ${row.returnId}`}
                                  onClick={() => onReject(row)}
                                  disabled={rejectDisabled}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </Box>
  );
}
